"use client";

/**
 * SetupLayout Component
 *
 * Split-screen layout wrapper for the setup flow:
 * - Desktop (lg+): 55% form (left) / 45% social proof (right)
 * - Mobile: Full-width form, social proof hidden
 * - Payment step: Full-width form, social proof hidden
 *
 * IMPORTANT: Layout changes are INSTANT (no animation).
 * Only content animates via BlurTransition. This prevents
 * horrible mid-transition states when switching layouts.
 *
 * Header design (Fyxer.ai style):
 * - Row 1: Back button (left) | Logo (centered) | empty (right)
 * - Row 2: Full-width segmented progress bar (only from step 1+)
 */
import { c as _c } from "react/compiler-runtime";
import { ReactNode } from "react";
import Link from "next/link";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeftIcon } from "@heroicons/react/20/solid";
import { T, useGT } from "gt-next";
import SocialProofPanel from "./SocialProofPanel";
import SetupHelpLink from "./SetupHelpLink";
import type { SetupStep } from "../types";
interface SetupLayoutProps {
  children: ReactNode;
  /** Current step for the indicator */
  currentStep: SetupStep;
  /** Total number of steps */
  totalSteps: number;
  /** Whether to show the back button */
  showBackButton?: boolean;
  /** Back button handler */
  onBack?: () => void;
  /** Custom label for the back button (default: "Back") */
  backButtonLabel?: string;
  /** Whether user is logged in (affects step count) */
  isLoggedIn?: boolean;
  /** Whether to use full-width layout (hides social proof panel) */
  fullWidth?: boolean;
}

// Shared transition config
const TRANSITION = {
  duration: 0.25,
  ease: [0.22, 1, 0.36, 1] as const
};
export default function SetupLayout(t0) {
  const $ = _c(48);
  const {
    children,
    currentStep,
    totalSteps,
    showBackButton: t1,
    onBack,
    backButtonLabel,
    isLoggedIn: t2,
    fullWidth: t3
  } = t0;
  const showBackButton = t1 === undefined ? true : t1;
  const isLoggedIn = t2 === undefined ? false : t2;
  const fullWidth = t3 === undefined ? false : t3;
  const gt = useGT();
  const showProgressBar = currentStep > 0;
  const t4 = `flex flex-col bg-white ${fullWidth ? "w-full" : "w-full lg:flex-[0_0_55%]"}`;
  let t5;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      minWidth: 0
    };
    $[0] = t5;
  } else {
    t5 = $[0];
  }
  const t6 = `mx-auto w-full px-5 pt-5 sm:px-6 lg:px-0 lg:pt-6 ${fullWidth ? "max-w-5xl" : "max-w-md"}`;
  let t7;
  if ($[1] !== backButtonLabel || $[2] !== currentStep || $[3] !== gt || $[4] !== onBack || $[5] !== showBackButton) {
    t7 = showBackButton && currentStep > 0 && onBack && <motion.button key="back-button" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={TRANSITION} onClick={onBack} className="flex items-center gap-0.5 text-sm font-medium text-black/50 transition-colors hover:text-black"><ChevronLeftIcon className="h-5 w-5" /><span>{backButtonLabel ?? gt("Back")}</span></motion.button>;
    $[1] = backButtonLabel;
    $[2] = currentStep;
    $[3] = gt;
    $[4] = onBack;
    $[5] = showBackButton;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== t7) {
    t8 = <div className="absolute left-0 top-1/2 -translate-y-1/2"><AnimatePresence mode="wait">{t7}</AnimatePresence></div>;
    $[7] = t7;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"><Link href="/" className="block transition-opacity duration-150 ease-out hover:opacity-70"><Image src="/img/outfame-logo-full.svg" alt="Outfame" width={140} height={32} className="h-7 w-auto" priority={true} /></Link></div>;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== currentStep || $[11] !== isLoggedIn) {
    t10 = !isLoggedIn && currentStep === 0 && <motion.div key="sign-in" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={TRANSITION}><Link href="/login" className="text-sm font-medium text-black/50 transition-colors hover:text-black"><T>Sign in</T></Link></motion.div>;
    $[10] = currentStep;
    $[11] = isLoggedIn;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== t10) {
    t11 = <div className="absolute right-0 top-1/2 -translate-y-1/2"><AnimatePresence mode="wait">{t10}</AnimatePresence></div>;
    $[13] = t10;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== t11 || $[16] !== t8) {
    t12 = <div className="relative flex h-8 items-center">{t8}{t9}{t11}</div>;
    $[15] = t11;
    $[16] = t8;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== currentStep || $[19] !== isLoggedIn || $[20] !== showProgressBar || $[21] !== totalSteps) {
    t13 = showProgressBar && <motion.div key="progress-bar" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={TRANSITION} className="mt-5"><ProgressBar currentStep={currentStep} totalSteps={totalSteps} isLoggedIn={isLoggedIn} /></motion.div>;
    $[18] = currentStep;
    $[19] = isLoggedIn;
    $[20] = showProgressBar;
    $[21] = totalSteps;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t13) {
    t14 = <AnimatePresence mode="wait">{t13}</AnimatePresence>;
    $[23] = t13;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== t12 || $[26] !== t14 || $[27] !== t6) {
    t15 = <header className={t6}>{t12}{t14}</header>;
    $[25] = t12;
    $[26] = t14;
    $[27] = t6;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  const t16 = showProgressBar ? "h-6" : "h-5";
  let t17;
  if ($[29] !== t16) {
    t17 = <div className={t16} />;
    $[29] = t16;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  const t18 = `flex flex-1 flex-col px-5 sm:px-6 lg:px-10 ${currentStep === 1 ? "pb-48 lg:pb-8" : "pb-8"}`;
  const t19 = `mx-auto flex w-full flex-1 flex-col ${fullWidth ? "max-w-5xl" : "max-w-md"}`;
  let t20;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <SetupHelpLink />;
    $[31] = t20;
  } else {
    t20 = $[31];
  }
  let t21;
  if ($[32] !== children || $[33] !== t19) {
    t21 = <div className={t19}>{children}{t20}</div>;
    $[32] = children;
    $[33] = t19;
    $[34] = t21;
  } else {
    t21 = $[34];
  }
  let t22;
  if ($[35] !== t18 || $[36] !== t21) {
    t22 = <main className={t18}>{t21}</main>;
    $[35] = t18;
    $[36] = t21;
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  let t23;
  if ($[38] !== t15 || $[39] !== t17 || $[40] !== t22 || $[41] !== t4) {
    t23 = <div className={t4} style={t5}>{t15}{t17}{t22}</div>;
    $[38] = t15;
    $[39] = t17;
    $[40] = t22;
    $[41] = t4;
    $[42] = t23;
  } else {
    t23 = $[42];
  }
  let t24;
  if ($[43] !== fullWidth) {
    t24 = !fullWidth && <div className="hidden lg:block" style={{
      width: "45%",
      minWidth: 0
    }}><SocialProofPanel className="h-full" /></div>;
    $[43] = fullWidth;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[45] !== t23 || $[46] !== t24) {
    t25 = <div className="flex min-h-screen w-full overflow-hidden">{t23}{t24}</div>;
    $[45] = t23;
    $[46] = t24;
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  return t25;
}

/**
 * Fyxer.ai-style segmented progress bar
 *
 * Design specs:
 * - Full width of content area
 * - Rounded pill segments with gaps
 * - Completed + current = filled (black)
 * - Remaining = light gray (black/10)
 * - Smooth width animations
 */
function ProgressBar(t0) {
  const $ = _c(10);
  const {
    currentStep,
    totalSteps,
    isLoggedIn
  } = t0;
  let t1;
  if ($[0] !== isLoggedIn) {
    t1 = step => {
      if (isLoggedIn && step === 3) {
        return 2;
      }
      return step;
    };
    $[0] = isLoggedIn;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const getDisplayIndex = t1;
  let t2;
  if ($[2] !== currentStep || $[3] !== getDisplayIndex) {
    t2 = getDisplayIndex(currentStep);
    $[2] = currentStep;
    $[3] = getDisplayIndex;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const displayIndex = t2;
  let t3;
  if ($[5] !== totalSteps) {
    t3 = Array.from({
      length: totalSteps
    });
    $[5] = totalSteps;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== displayIndex || $[8] !== t3) {
    t4 = <div className="flex w-full items-center gap-1.5">{t3.map((_, index) => {
        const isCompleted = index < displayIndex;
        const isCurrent = index === displayIndex;
        const isFilled = isCompleted || isCurrent;
        return <motion.div key={index} className="h-1.5 flex-1 rounded-full" initial={false} animate={{
          backgroundColor: isFilled ? "#141414" : "rgba(0, 0, 0, 0.1)"
        }} transition={{
          duration: 0.3,
          ease: [0.4, 0, 0.2, 1]
        }} />;
      })}</div>;
    $[7] = displayIndex;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}